import { useState, useRef, useCallback, useEffect } from 'react';
import { COLORS, INPUT_TOKENS, DETAIL_TOKENS } from '../../../styles/UISystem';
import { IconCircleButton } from '../Icons/IconCircleButton';
import { PhoneInputField } from './PhoneInputField';
import { X } from '@phosphor-icons/react/dist/ssr/X';
import { ChatCircle } from '@phosphor-icons/react/dist/ssr/ChatCircle';
import { ALL_CONTACT_TYPES, openContactLink } from '../../../utils/contactTypeConfig';
import { formatPhoneNumber } from '../../../utils/sharingFields';
import { fitTextSize } from '../../../utils/fitText';
import DetailSectionCard from '../Sections/DetailSectionCard';

/**
 * Get the base type from a field key (e.g., 'phone2' -> 'phone')
 */
const getBaseType = (fieldKey) => {
  return fieldKey.replace(/\d+$/, '');
};

/**
 * ContactField Component
 *
 * Single contact field with icon, input, and clear button.
 * Handles different contact types with appropriate input methods and link behavior.
 *
 * @param {string} type - Contact type (phone, email, instagram, etc.)
 * @param {string} value - Current value
 * @param {string} fieldKey - Storage key (phone, phone2, email, etc.)
 * @param {boolean} disabled - Whether field is read-only
 * @param {boolean} showLinks - Whether icon should be clickable to open link
 * @param {function} onChange - Called on blur with new value
 * @param {function} onClear - Called when clear button is clicked
 * @param {boolean} compact - Detail-page treatment: narrower row, centred value
 */
const ContactField = ({
  type,
  value = '',
  // fieldKey - passed by parent for tracking, not used internally
  disabled = false,
  showLinks = false,
  onChange,
  onClear,
  autoFocus = false,
  compact = false,
}) => {
  // Get config for this contact type
  const baseType = getBaseType(type);
  const config = ALL_CONTACT_TYPES[baseType] || ALL_CONTACT_TYPES.custom;
  const IconComponent = config.Icon;

  // Local state for input value (to avoid calling onChange on every keystroke)
  const [localValue, setLocalValue] = useState(value);
  const [showCopied, setShowCopied] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  // Auto-focus on mount if autoFocus prop is true
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      // Small delay to ensure overlay animation has started
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [autoFocus]);

  // Sync local value when prop changes (e.g., external updates)
  // Using a ref to track if we should sync
  const lastPropValue = useRef(value);
  if (value !== lastPropValue.current) {
    lastPropValue.current = value;
    if (value !== localValue) {
      setLocalValue(value);
    }
  }

  // Handle focus
  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  // Handle blur - call onChange with cleaned value
  const handleBlur = useCallback(() => {
    setIsFocused(false);
    let cleanedValue = localValue;

    // Strip @ prefix if user typed it (for @ prefix types)
    if (config.hasAtPrefix) {
      cleanedValue = cleanedValue.replace(/^@/, '').toLowerCase();
    }

    // Strip LinkedIn URL if pasted
    if (baseType === 'linkedin') {
      cleanedValue = cleanedValue
        .toLowerCase()
        .replace(/^(https?:\/\/)?(www\.)?linkedin\.com\/in\//i, '')
        .replace(/\/$/, '');
    }

    // Only call onChange if value actually changed
    if (cleanedValue !== value) {
      onChange?.(cleanedValue);
    }
  }, [localValue, value, config.hasAtPrefix, baseType, onChange]);

  // Handle input change (for text inputs)
  const handleInputChange = (e) => {
    setLocalValue(e.target.value);
  };

  // Handle phone input change (PhoneInputField returns E.164 directly)
  const handlePhoneChange = (newValue) => {
    setLocalValue(newValue);
  };

  // Handle phone blur
  const handlePhoneBlur = () => {
    setIsFocused(false);
    if (localValue !== value) {
      onChange?.(localValue);
    }
  };

  // Handle clear button
  const handleClear = () => {
    setLocalValue('');
    onClear?.();
    // Focus input after clearing
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  // Handle icon click (link or copy)
  const handleIconClick = () => {
    if (!showLinks || !value) return;

    if (config.getLink) {
      const link = config.getLink(value);
      if (link) {
        openContactLink(link);
        return;
      }
    }

    // Fall back to copy if copyOnly is set or getLink returned null
    if (config.copyOnly) {
      navigator.clipboard.writeText(value).then(() => {
        setShowCopied(true);
        setTimeout(() => setShowCopied(false), 2000);
      });
    }
  };

  // Determine if icon should be clickable
  const isIconClickable = showLinks && value && (config.getLink || config.copyOnly);

  // Determine if we should show the field at all
  // Disabled + empty = hide the field
  if (disabled && !value) {
    return null;
  }

  // Render phone input
  const renderPhoneInput = () => (
    <div
      style={{
        flex: 1,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <PhoneInputField
        ref={inputRef}
        value={localValue}
        onChange={handlePhoneChange}
        onFocus={handleFocus}
        onBlur={handlePhoneBlur}
        placeholder={config.placeholder}
        disabled={disabled}
        embedded={true} // Removes internal padding/positioning for tight fit
        fontSize={17} // Match ContactField's text input size
        // Plain national number at rest; the country picker appears on focus.
        hideDialCode={compact && !isFocused}
        // Must be inputStyle: `style` lands on PhoneInputField's container,
        // where textAlign does nothing for the value inside it.
        inputStyle={compact && !isFocused ? { textAlign: 'center' } : undefined}
      />
    </div>
  );

  // What a LOCKED row displays. A disabled row isn't an input — rendering one
  // dragged PhoneInputField's dial-code pill into a 236px row and clipped the
  // number mid-digits (device pass, 2026-08-19). The frames show a plain
  // national-format string, so locked rows render text.
  const lockedText = (() => {
    if (config.category === 'phone-like') return formatPhoneNumber(localValue);
    // No @ on locked rows either — Instagram now matches LinkedIn, which
    // never had one.

    return localValue;
  })();

  // Long values shrink rather than clip — a truncated phone number or handle
  // hides the part that identifies it. Same call the venue pills make.
  // Usable width = row minus the icon slot, and minus the SMS slot when shown.
  const showsSmsAction = baseType === 'phone' && showLinks && value && !isFocused;
  // 280 row minus the icon slot, minus the SMS slot when it shows. Only a
  // genuinely unbounded value (a long email) should ever shrink now — ordinary
  // numbers and handles fit at full size.
  const lockedFontSize = fitTextSize(lockedText, {
    width: 280 - 60 - (showsSmsAction ? 60 : 16),
    max: 17,
    min: 12,
  });

  const renderLockedValue = () => (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        textAlign: compact ? 'center' : 'left',
        fontSize: `${compact ? lockedFontSize : 17}px`,
        color: COLORS.MEDIUM_GRAY,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {lockedText}
    </div>
  );

  // No @ prefix on a compact row (user, 2026-08-19): the icon and the
  // placeholder already say which platform it is, LinkedIn never carried one,
  // and the prefix is what left a stranded "@" in the empty and focused states.
  // handleBlur still strips a typed @, so the stored value is unaffected.
  const renderTextInput = () => (
    <>
      {config.hasAtPrefix && !compact && (
        <span
          style={{
            fontSize: '17px',
            color: COLORS.MEDIUM_GRAY,
            marginRight: '4px',
          }}
        >
          @
        </span>
      )}
      <div
        style={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <input
          ref={inputRef}
          type={config.inputMode === 'email' ? 'email' : 'text'}
          value={localValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={config.placeholder}
          disabled={disabled}
          autoCapitalize="none"
          autoCorrect="off"
          style={{
            width: '100%',
            border: 'none',
            fontSize: '17px',
            // Centred at rest, left-aligned once focused: centred text shifts
            // under the caret on every keystroke, which is most of why editing a
            // number felt awkward. The change also signals you're in edit mode
            // rather than reading (user, 2026-08-19).
            textAlign: compact && !isFocused ? 'center' : 'left',
            textOverflow: 'ellipsis',
            // Lighter text when disabled
            color: disabled ? COLORS.MEDIUM_GRAY : COLORS.DARK_GRAY,
            backgroundColor: 'transparent',
            outline: 'none',
            opacity: 1, // Override browser disabled opacity
            ...(disabled ? { WebkitTextFillColor: COLORS.MEDIUM_GRAY } : {}),
          }}
        />
      </div>
    </>
  );

  return (
    <div
      style={{
        // 10px between rows on a detail page (measured off the frames); the
        // roomier 16px stays for the form context.
        marginBottom: compact ? '10px' : '16px',
        position: 'relative',
        // Percentage, not a fixed width: the row must stay proportionally
        // narrower than the section cards on every handset (see DETAIL_TOKENS).
        ...(compact
          ? {
              width: DETAIL_TOKENS.ROW_WIDTH,
              maxWidth: DETAIL_TOKENS.ROW_MAX_WIDTH,
              minWidth: DETAIL_TOKENS.ROW_MIN_WIDTH,
              marginLeft: 'auto',
              marginRight: 'auto',
            }
          : {}),
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          // 52px tall on a detail page: 8px around the 36px icon slot. Clears
          // the 44pt tap-target floor with room, and reads tighter than the old
          // 60px. The frames disagree with each other here (43 vs 59), so the
          // tap target decides rather than either drawing.
          padding: compact ? '8px 16px' : '12px 16px',
          borderRadius: INPUT_TOKENS.CONTACT_RADIUS,
          // Locked = the peer owns this value; editable = the viewer's own entry
          border: disabled ? INPUT_TOKENS.CONTACT_BORDER_LOCKED : INPUT_TOKENS.CONTACT_BORDER,
          backgroundColor: disabled
            ? INPUT_TOKENS.CONTACT_BG_LOCKED
            : INPUT_TOKENS.CONTACT_BG_EDITABLE,
          boxSizing: 'border-box',
          opacity: 1,
        }}
      >
        {/* Icon - Fixed 36x36 slot */}
        <div style={{ marginRight: '8px', position: 'relative' }}>
          {/* Compact rows draw the glyph bare on the grey pill — the white
              circle belongs to the icon ROW under the name, not to these rows
              (2026-08-18 frames; the disc only became visible once the row
              turned grey). */}
          <IconCircleButton
            icon={IconComponent}
            buttonSize={36}
            size={24}
            inline={!isIconClickable}
            onClick={isIconClickable ? handleIconClick : undefined}
            backgroundColor={compact || !isIconClickable ? 'transparent' : undefined}
            iconColor={isIconClickable ? COLORS.DARK_GRAY : COLORS.MEDIUM_GRAY}
          />

          {/* Copied tooltip */}
          {showCopied && (
            <div
              style={{
                position: 'absolute',
                top: '-30px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: COLORS.DARK_GRAY,
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                whiteSpace: 'nowrap',
              }}
            >
              Copied!
            </div>
          )}
        </div>

        {/* Value — locked rows are text, editable rows are inputs */}
        {disabled
          ? renderLockedValue()
          : config.category === 'phone-like'
            ? renderPhoneInput()
            : renderTextInput()}

        {/* Right slot — on a compact row this is ALWAYS reserved, even when
            empty, so it mirrors the icon slot on the left. Without the mirror
            the value centres in the leftover space rather than in the pill, and
            a row with the SMS action sits visibly off-centre from one without
            it (user, 2026-08-19). */}
        {compact && !showsSmsAction && !(!disabled && localValue && isFocused) && (
          <div style={{ width: '44px', flexShrink: 0 }} aria-hidden="true" />
        )}

        {/* Text/SMS icon - only for actual phone type (not WhatsApp/Signal) */}
        {/* Hide when focused (editing) to prevent accidental texts */}
        {baseType === 'phone' && showLinks && value && !isFocused && (
          <div style={{ marginLeft: '8px', width: '36px', flexShrink: 0 }}>
            <IconCircleButton
              icon={ChatCircle}
              buttonSize={36}
              size={22}
              onClick={() => {
                window.location.href = `sms:${value}`;
              }}
              backgroundColor={compact ? 'transparent' : undefined}
              iconColor={COLORS.DARK_GRAY}
            />
          </div>
        )}

        {/* Clear button — editing only. At rest the row is something you read;
            the X belongs to the mode where you're changing it (user, 2026-08-19). */}
        {!disabled && localValue && isFocused && (
          <button
            // preventDefault keeps the input focused. Without it the mousedown
            // blurs the input, isFocused flips false, and this button unmounts
            // before its click ever fires — so the X did nothing at all. Same
            // guard NicknameField's clear button uses.
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleClear}
            style={{
              background: 'none',
              border: 'none',
              padding: '2px',
              marginLeft: compact ? '8px' : '4px',
              ...(compact ? { width: '36px', flexShrink: 0 } : {}),
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: COLORS.MEDIUM_GRAY,
            }}
            aria-label="Clear"
          >
            <X size={16} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * ContactFieldExample — every variation on one screen (user instruction,
 * 2026-08-19). ContactField is tricky precisely because one component serves
 * two jobs: a locked row that acts as a LINK, and an editable row that acts as
 * a FIELD. The detail-page bugs (white icon discs on grey, a clipped phone
 * number) were both invisible until the variants sat side by side, so this is
 * where the component gets judged from now on.
 */
export const ContactFieldExample = () => {
  const [phone, setPhone] = useState('+19176502256');
  const [email, setEmail] = useState('bob@email.com');
  const [instagram, setInstagram] = useState('bobmcgoo');
  const [emptyPhone, setEmptyPhone] = useState('');
  const [emptyIg, setEmptyIg] = useState('');
  const [emptyLi, setEmptyLi] = useState('');

  const H = ({ children, note }) => (
    <div style={{ marginTop: '28px', marginBottom: '10px' }}>
      <div style={{ fontSize: '15px', fontWeight: 600, color: COLORS.DARK_GRAY }}>{children}</div>
      {note && (
        <div style={{ fontSize: '12px', color: COLORS.MEDIUM_GRAY, marginTop: '2px' }}>{note}</div>
      )}
    </div>
  );

  // The detail page's ground: rows sit on white, section cards on LIGHT_GRAY.
  const Stage = ({ children }) => (
    <div style={{ backgroundColor: '#FFFFFF', padding: '12px 0', borderRadius: '16px' }}>
      {children}
    </div>
  );

  return (
    <div style={{ padding: '20px', maxWidth: '420px', margin: '0 auto' }}>
      <h2 style={{ color: COLORS.DARK_GRAY, marginBottom: '4px', textAlign: 'center' }}>
        ContactField
      </h2>
      <p
        style={{
          color: COLORS.MEDIUM_GRAY,
          marginBottom: '8px',
          textAlign: 'center',
          fontSize: '13px',
        }}
      >
        Locked rows are links. Editable rows are fields. Same component.
      </p>

      <H note="disabled + showLinks + compact. Grey pill, bare glyph, centred value. Icons still open the app; the phone row still texts.">
        1 · Locked (detail page)
      </H>
      <Stage>
        <ContactField
          type="phone"
          value="+19176502256"
          fieldKey="phone"
          disabled
          showLinks
          compact
        />
        <ContactField
          type="instagram"
          value="markesan"
          fieldKey="instagram"
          disabled
          showLinks
          compact
        />
        <ContactField
          type="linkedin"
          value="markmangan"
          fieldKey="linkedin"
          disabled
          showLinks
          compact
        />
        <ContactField
          type="email"
          value="bob@email.com"
          fieldKey="email"
          disabled
          showLinks
          compact
        />
      </Stage>

      <H note="Shrink-to-fit rather than truncate — a clipped number hides the digits that identify it. Compare the UK and Australian numbers against the US one above.">
        2 · Locked, long + foreign values
      </H>
      <Stage>
        <ContactField
          type="phone"
          value="+442079460958"
          fieldKey="phone"
          disabled
          showLinks
          compact
        />
        <ContactField
          type="phone"
          value="+61293744000"
          fieldKey="phone"
          disabled
          showLinks
          compact
        />
        <ContactField
          type="email"
          value="mark.mangan.longaddress@verylongdomainname.com"
          fieldKey="email"
          disabled
          showLinks
          compact
        />
        <ContactField
          type="instagram"
          value="averylonginstagramhandle_2026"
          fieldKey="instagram"
          disabled
          showLinks
          compact
        />
      </Stage>

      <H note="White pill with a hairline. The viewer's own entry, or an unclaimed peer's field. Tap to edit; X clears. The phone shows a plain national number until focused, when the country picker appears.">
        3 · Editable (detail page)
      </H>
      <Stage>
        <ContactField
          type="phone"
          value={phone}
          fieldKey="phone"
          showLinks
          compact
          onChange={setPhone}
          onClear={() => setPhone('')}
        />
        <ContactField
          type="instagram"
          value={instagram}
          fieldKey="instagram"
          showLinks
          compact
          onChange={setInstagram}
          onClear={() => setInstagram('')}
        />
      </Stage>

      <H note="The state the detail pages never show (they pass starterFields=[]) but your own profile lives in. This is where a centred narrow pill is most likely to read wrong — judge it before ContactField takes over ProfileForm.">
        4 · Empty — your own profile, before anything is filled in
      </H>
      {/* Stateful, so typing and clearing behave exactly as they will on the
          real profile — an example with a hardcoded value can look right while
          the X does nothing. */}
      <Stage>
        <ContactField
          type="phone"
          value={emptyPhone}
          fieldKey="phone"
          compact
          onChange={setEmptyPhone}
          onClear={() => setEmptyPhone('')}
        />
        <ContactField
          type="instagram"
          value={emptyIg}
          fieldKey="instagram"
          compact
          onChange={setEmptyIg}
          onClear={() => setEmptyIg('')}
        />
        <ContactField
          type="linkedin"
          value={emptyLi}
          fieldKey="linkedin"
          compact
          onChange={setEmptyLi}
          onClear={() => setEmptyLi('')}
        />
      </Stage>

      <H note="AddContactOverlay still renders full-width, left-aligned rows — it's a single field in an overlay, not a column. ProfileForm no longer does: it uses the same treatment as 3 and 4.">
        5 · Full-width row (overlay context)
      </H>
      <ContactField
        type="phone"
        value={phone}
        fieldKey="phone"
        onChange={setPhone}
        onClear={() => setPhone('')}
      />
      <ContactField
        type="email"
        value={email}
        fieldKey="email"
        onChange={setEmail}
        onClear={() => setEmail('')}
      />

      <H note="Locked rows against the section containers they sit between, so the shapes can be judged together — radius, padding, and the width relationship.">
        6 · Rows against the section containers
      </H>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
        <DetailSectionCard label="Lives in">
          <div style={{ fontSize: '16px', color: '#000000', textAlign: 'center' }}>
            East Village, NYC
          </div>
        </DetailSectionCard>
        <DetailSectionCard>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ fontSize: '16px', color: '#000000', textAlign: 'center' }}>
              <span style={{ fontWeight: 600 }}>Designer</span> @ Acme Design Inc
            </div>
            <div style={{ fontSize: '16px', color: '#000000' }}>
              I build lots of things for people everywhere with my hands and rigor
            </div>
            <div style={{ fontSize: '16px', color: '#999999', textAlign: 'center' }}>
              SF, Thailand, LA
            </div>
          </div>
        </DetailSectionCard>
        <div style={{ width: '100%' }}>
          <ContactField
            type="phone"
            value="+19176502256"
            fieldKey="phone"
            disabled
            showLinks
            compact
          />
          <ContactField
            type="instagram"
            value="markesan"
            fieldKey="instagram"
            disabled
            showLinks
            compact
          />
        </div>
      </div>
    </div>
  );
};

export default ContactField;
